import React from 'react';
import Modal from './Modal';
import { CheckCircleIcon } from './Icons';
import { formatNumber } from '../../utils/helpers';

import { Unit } from '../../types';

export interface StatementUpdate {
    id: string;
    UnitID: string;
    amount: number;
    expectedAmount?: number;
    desc: string;
    selected: boolean;
    isDuplicate?: boolean;
}

interface StatementConfirmModalProps {
    updates: StatementUpdate[];
    allUnits: Unit[];
    expectedAmountMap?: Map<string, number>;
    onConfirm: (selectedUpdates: StatementUpdate[]) => void;
    onCancel: () => void;
    isSaving: boolean;
}

const StatementConfirmModal: React.FC<StatementConfirmModalProps> = ({ updates: initialUpdates, allUnits, expectedAmountMap, onConfirm, onCancel, isSaving }) => {
    const [updates, setUpdates] = React.useState<StatementUpdate[]>(initialUpdates);
    
    const toggleSelect = (id: string) => {
        setUpdates(prev => prev.map(u => u.id === id ? { ...u, selected: !u.selected } : u));
    };

    const toggleSelectAll = () => {
        const allSelected = updates.every(u => u.selected);
        setUpdates(prev => prev.map(u => ({ ...u, selected: !allSelected })));
    };

    const handleEdit = (id: string, field: 'UnitID' | 'amount', value: string | number) => {
        setUpdates(prev => prev.map(u => {
            if (u.id === id) {
                const newUpdate = { ...u, [field]: value };
                if (field === 'UnitID' && expectedAmountMap) {
                    newUpdate.expectedAmount = expectedAmountMap.get(value as string) || 0;
                }
                return newUpdate;
            }
            return u;
        }));
    };

    const selectedUpdates = updates.filter(u => u.selected);
    const totalAmount = selectedUpdates.reduce((sum, u) => sum + u.amount, 0);
    const hasUnassignedSelected = selectedUpdates.some(u => !u.UnitID);

    return (
        <Modal title="Xác nhận đối soát sao kê" onClose={onCancel} size="5xl">
            <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex justify-between items-center">
                    <div>
                        <p className="text-sm text-blue-800 font-semibold">Tổng số giao dịch: {selectedUpdates.length} / {updates.length}</p>
                        <p className="text-xs text-blue-600 mt-1">Hệ thống tự động nhận diện căn hộ. Các dòng <span className="text-rose-600 font-bold">màu đỏ</span> cần gán căn hộ thủ công.</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-blue-600 uppercase font-bold tracking-wider">Tổng tiền được chọn</p>
                        <p className="text-2xl font-black text-blue-700">{formatNumber(totalAmount)}</p>
                    </div>
                </div>

                <div className="max-h-[500px] overflow-y-auto border border-gray-200 rounded-xl shadow-inner">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left w-10">
                                    <input 
                                        type="checkbox" 
                                        checked={updates.length > 0 && updates.every(u => u.selected)}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left font-bold text-gray-600 w-32">Căn hộ</th>
                                <th className="px-4 py-3 text-right font-bold text-gray-600 w-40">Số tiền</th>
                                <th className="px-4 py-3 text-left font-bold text-gray-600">Nội dung giao dịch</th>
                                <th className="px-4 py-3 text-center font-bold text-gray-600 w-24">Trạng thái</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {updates.map((update) => (
                                <tr key={update.id} className={`hover:bg-gray-50 transition-colors ${!update.selected ? 'opacity-50 grayscale' : ''} ${update.isDuplicate ? 'bg-amber-50/50' : ''} ${!update.UnitID && update.selected ? 'bg-rose-50' : ''}`}>
                                    <td className="px-4 py-3">
                                        <input 
                                            type="checkbox" 
                                            checked={update.selected}
                                            onChange={() => toggleSelect(update.id)}
                                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <select 
                                            value={update.UnitID}
                                            onChange={(e) => handleEdit(update.id, 'UnitID', e.target.value)}
                                            className={`w-full bg-white border rounded px-2 py-1 font-bold focus:ring-1 focus:ring-primary outline-none ${!update.UnitID ? 'border-rose-500 text-rose-600' : 'border-gray-300 text-gray-900'}`}
                                        >
                                            <option value="">-- Chưa gán --</option>
                                            {allUnits.map(u => (
                                                <option key={u.UnitID} value={u.UnitID}>{u.UnitID}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="relative">
                                            <div className="flex items-center justify-end gap-1">
                                                <input 
                                                    type="text"
                                                    value={formatNumber(update.amount)}
                                                    onChange={(e) => {
                                                        const raw = e.target.value.replace(/[^0-9]/g, '');
                                                        handleEdit(update.id, 'amount', parseInt(raw) || 0);
                                                    }}
                                                    className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-right font-bold text-emerald-600 focus:ring-1 focus:ring-primary outline-none"
                                                />
                                                {update.UnitID && update.expectedAmount !== undefined && update.amount === update.expectedAmount && (
                                                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                                )}
                                            </div>
                                            <div className="text-[10px] mt-0.5 flex justify-end">
                                                {update.UnitID && update.expectedAmount !== undefined && (
                                                    <span className="italic font-medium text-rose-500">Phải nộp: {formatNumber(update.expectedAmount)}</span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 text-xs italic">
                                        {update.desc}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {!update.UnitID && update.selected ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 uppercase tracking-tighter">
                                                Cần gán
                                            </span>
                                        ) : update.isDuplicate ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 uppercase tracking-tighter">
                                                Đã có tiền
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-tighter">
                                                Hợp lệ
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button 
                        onClick={onCancel} 
                        disabled={isSaving}
                        className="px-5 py-2.5 bg-gray-100 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        Hủy bỏ
                    </button>
                    <button 
                        onClick={() => onConfirm(selectedUpdates)} 
                        disabled={isSaving || selectedUpdates.length === 0 || hasUnassignedSelected}
                        className="px-6 py-2.5 bg-primary text-white text-sm font-bold rounded-xl shadow-lg hover:bg-primary-focus flex items-center gap-2 disabled:opacity-50"
                    >
                        {isSaving ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <CheckCircleIcon className="w-5 h-5" />
                        )}
                        Xác nhận Import ({selectedUpdates.length})
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default StatementConfirmModal;
